import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Col,
  Row,
  Statistic,
  Button,
  Typography,
  Skeleton,
  Progress,
} from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import {
  getDashboardStats,
  getDailySubmissionVolumes,
  type KycDashboardStats,
} from '../../services/adminKycApiService';

const { Title } = Typography;

/** KYC overview dashboard: statistics, 7-day bar chart, queue entry point. */
export function KycDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<KycDashboardStats | null>(null);
  const [volumes, setVolumes] = useState<{ date: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [s, v] = await Promise.all([
        getDashboardStats(),
        getDailySubmissionVolumes(),
      ]);
      setStats(s);
      setVolumes(v);
    } catch {
      // stats remain null — skeletons stay visible
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 60_000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const chartData = volumes.map((v) => ({
    ...v,
    label: dayjs(v.date).format('DD/MM'),
  }));

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 24 }}>
        KYC Dashboard
      </Title>

      {/* Stat cards row 1 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {[
          {
            title: 'Pending',
            value: stats?.totalPending,
            icon: <ClockCircleOutlined />,
            color: '#faad14',
          },
          {
            title: 'Approved',
            value: stats?.totalApproved,
            icon: <CheckCircleOutlined />,
            color: '#52c41a',
          },
          {
            title: 'Rejected',
            value: stats?.totalRejected,
            icon: <CloseCircleOutlined />,
            color: '#ff4d4f',
          },
          {
            title: 'Manual Review',
            value: stats?.totalManualReview,
            icon: <ExclamationCircleOutlined />,
            color: '#fa8c16',
          },
        ].map((s) => (
          <Col key={s.title} xs={24} sm={12} md={6}>
            <Card bordered={false} style={{ borderRadius: 10 }}>
              <Skeleton loading={loading} active paragraph={false}>
                <Statistic
                  title={s.title}
                  value={s.value ?? 0}
                  prefix={s.icon}
                  valueStyle={{ color: s.color, fontWeight: 700 }}
                />
              </Skeleton>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Stat cards row 2 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <Card bordered={false} style={{ borderRadius: 10 }}>
            <Skeleton loading={loading} active paragraph={false}>
              <Statistic
                title="Avg AI Confidence"
                value={stats?.avgAiConfidence ?? 0}
                precision={1}
                suffix="%"
              />
              <Progress
                percent={Math.round(stats?.avgAiConfidence ?? 0)}
                size="small"
                strokeColor={
                  (stats?.avgAiConfidence ?? 0) >= 80
                    ? '#52c41a'
                    : (stats?.avgAiConfidence ?? 0) >= 50
                    ? '#faad14'
                    : '#ff4d4f'
                }
                style={{ marginTop: 8 }}
                showInfo={false}
              />
            </Skeleton>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card bordered={false} style={{ borderRadius: 10 }}>
            <Skeleton loading={loading} active paragraph={false}>
              <Statistic
                title="Today's Submissions"
                value={stats?.todaySubmissions ?? 0}
                valueStyle={{ color: '#1677ff', fontWeight: 700 }}
              />
            </Skeleton>
          </Card>
        </Col>
      </Row>

      {/* 7-day bar chart */}
      <Card
        title="Submission volume — last 7 days"
        bordered={false}
        style={{ borderRadius: 10, marginBottom: 24 }}
      >
        <Skeleton loading={loading} active>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#1677ff" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Skeleton>
      </Card>

      <Button
        type="primary"
        size="large"
        onClick={() => navigate('/kyc/queue')}
      >
        Review Queue
      </Button>
    </div>
  );
}

export default KycDashboardPage;
